import { useEffect, useId, useRef, useState } from 'react'
import { useAccent } from './AccentProvider'
import { ACCENT_PRESETS, CUSTOM_INPUT_FALLBACK, isValidHex } from './accentPreference'

export interface AccentPickerProps {
  /**
   * `compact` — a single current-accent trigger button that opens the swatch
   * grid in a popover, for the top bar's "Appearance" control (design doc §5).
   * The grid is NOT rendered inline in the top bar (that floods the header —
   * IB-262). `full` — the swatch grid + labels rendered inline, for a
   * Settings/Preferences surface (`AdminSettingsScreen`'s Preferences tab).
   */
  variant?: 'compact' | 'full'
}

/**
 * Accent colour picker (IB-258) — "Default (Graphite)" + the 30 curated
 * presets (`ACCENT_PRESETS`, `accentPreference.ts` — the sanctioned
 * data-module source of literal hex, read here as data) + a free-choice
 * custom colour input. Every control applies immediately (CLAUDE.md §4 "no
 * dead controls") — no separate "Apply"/"Save" step.
 *
 * The swatch grid is a `radiogroup` (mutually exclusive, exactly one active),
 * individually `Tab`-reachable native `<button>`s — the same keyboard model
 * `ThemeToggle` uses. In `compact` mode the grid lives in a popover behind a
 * single trigger so the top bar stays uncluttered; in `full` mode it is
 * rendered inline.
 */
export function AccentPicker({ variant = 'compact' }: AccentPickerProps) {
  const { accent, setAccent } = useAccent()
  const customInputId = useId()
  const isCustomActive = accent !== null && !ACCENT_PRESETS.some((p) => p.hex === accent)

  const grid = (
    <>
      <div className="iw-accent-picker__grid" role="radiogroup" aria-label="Accent colour">
        <button
          type="button"
          role="radio"
          aria-checked={accent === null}
          title="Default (Graphite)"
          aria-label="Default (Graphite)"
          className={
            'iw-accent-picker__swatch iw-accent-picker__swatch--default' +
            (accent === null ? ' iw-accent-picker__swatch--active' : '')
          }
          onClick={() => setAccent(null)}
        />
        {ACCENT_PRESETS.map((preset) => {
          const active = accent === preset.hex
          return (
            <button
              key={preset.hex}
              type="button"
              role="radio"
              aria-checked={active}
              title={preset.name}
              aria-label={preset.name}
              className={'iw-accent-picker__swatch' + (active ? ' iw-accent-picker__swatch--active' : '')}
              style={{ background: preset.hex }}
              onClick={() => setAccent(preset.hex)}
            />
          )
        })}
      </div>
      {/* Deliberately outside the radiogroup — ARIA radiogroup children should be radios only;
          the custom colour input is a distinct, free-choice control, not a 31st preset. */}
      <label
        className={'iw-accent-picker__custom' + (isCustomActive ? ' iw-accent-picker__custom--active' : '')}
        htmlFor={customInputId}
      >
        <input
          id={customInputId}
          type="color"
          className="iw-accent-picker__custom-input"
          value={isCustomActive && isValidHex(accent) ? accent : CUSTOM_INPUT_FALLBACK}
          onChange={(event) => setAccent(event.target.value)}
          aria-label="Custom colour"
          title="Custom…"
        />
        <span className={variant === 'full' ? undefined : 'iw-visually-hidden'}>Custom…</span>
      </label>
    </>
  )

  if (variant === 'full') {
    return <div className="iw-accent-picker">{grid}</div>
  }

  return <CompactAccentPicker accent={accent}>{grid}</CompactAccentPicker>
}

/**
 * Compact wrapper — a single trigger swatch (the current accent) that toggles
 * the grid in a popover. Closes on outside-click and Escape; stays open on a
 * swatch pick so the user can compare colours live. This is the top-bar form;
 * without the popover the 30-swatch grid rendered inline and flooded the
 * header (IB-262).
 */
function CompactAccentPicker({
  accent,
  children,
}: {
  accent: string | null
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // The trigger shows the live accent; graphite default uses the neutral token
  // (never a literal hex here — the default swatch's colour lives in CSS).
  const triggerStyle = accent !== null ? { background: accent } : undefined

  return (
    <div className="iw-accent-picker iw-accent-picker--compact" ref={rootRef}>
      <button
        type="button"
        className={
          'iw-accent-picker__trigger' + (accent === null ? ' iw-accent-picker__trigger--default' : '')
        }
        style={triggerStyle}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Appearance — accent colour"
        title="Appearance — accent colour"
        onClick={() => setOpen((v) => !v)}
      />
      {open ? (
        <div className="iw-accent-picker__popover" role="dialog" aria-label="Accent colour">
          {children}
        </div>
      ) : null}
    </div>
  )
}
